/** @format */
import {
    ChoiceChip,
    ChoiceChipVariant,
} from '@mpb-com/feather/components/atoms/choice-chip';
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { Input } from '@mpb-com/feather/components/molecules/input';
import { InputProps } from '@mpb-com/feather/components/molecules/input/types';
import { createClassNames } from '@mpb-com/feather/helpers/styles';
import { useTranslation } from 'next-i18next';
import React, {
    FunctionComponent,
    useContext,
    useEffect,
    useState,
} from 'react';
import styled from 'styled-components';

import PriceIcon from '@toucan/client/global/icons/tag.svg';

import {
    centToDecimal,
    getCurrencySymbol,
} from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market/context';

import { FilterOption } from '@toucan/client/features/search/config/types';

import { CustomRangeField, TEST_ID as dataTestId } from './constants';
import {
    getCustomRangeItem,
    isSelected,
    onCustomRangeInputKeyPress,
    parseLowestAndHighestPriceOption,
} from './helpers';
import { styles } from './price-filter.styles';
import { PriceFilterProps } from './types';

const StyledPriceFilter = styled('div')`
    ${styles}
`;

export const PriceFilter: FunctionComponent<PriceFilterProps> = ({
    filter,
    onPriceSelect,
    showTitle = true,
}) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);
    const currencySymbol = getCurrencySymbol(
        activeMarket.iso,
        activeMarket.currency
    );
    const [customRange, setCustomRange] = useState({ from: '', to: '' });

    const { selectedOptions } = filter;

    function resetCustomRange() {
        setCustomRange({ from: '', to: '' });
    }

    function handlePriceOptionSelect(
        value: FilterOption['value'],
        checked: boolean
    ) {
        resetCustomRange();

        const optionsToUpdate = filter.options.filter((option) => {
            if (option.value === value) {
                return checked;
            }
            return selectedOptions.some((selectedOption) => {
                return selectedOption.value === option.value;
            });
        });

        onPriceSelect(optionsToUpdate);
    }

    function onCustomRangeChange(value: string, field: CustomRangeField) {
        const from = field === CustomRangeField.FROM ? value : customRange.from;
        const to = field === CustomRangeField.TO ? value : customRange.to;

        setCustomRange({ from, to });

        const customRangeItem = getCustomRangeItem(from, to);

        if (!customRangeItem) {
            return;
        }

        onPriceSelect([
            { value: customRangeItem.value, label: `${from} - ${to}` },
        ]);
    }

    function setInitialCustomRange(options: FilterOption[]) {
        const { lowest, highest, isContinuous } =
            parseLowestAndHighestPriceOption(options);

        // don't set the custom range inputs unless the selected options form a continuous range
        if (!isContinuous) {
            return;
        }

        const from =
            lowest === Infinity ? '' : centToDecimal(lowest).toString();
        const to =
            highest === -Infinity ? '' : centToDecimal(highest).toString();

        setCustomRange({
            from,
            to,
        });
    }

    const commonInputProps: Partial<InputProps> = {
        className: 'input',
        type: 'number',
        prefixIcon: currencySymbol,
        min: 0,
        onKeyPress: onCustomRangeInputKeyPress,
    };

    useEffect(() => {
        setInitialCustomRange(selectedOptions);
    }, [selectedOptions]);

    return (
        <StyledPriceFilter data-testid={dataTestId} className="price-filter">
            <div
                className={createClassNames('price-filter__title', {
                    'price-filter__title--hidden': !showTitle,
                })}
            >
                <Icon
                    variant={IconVariant.CORE}
                    size={IconSize.XL}
                    className="price-filter__price-icon"
                >
                    <PriceIcon />
                </Icon>
                <p className="price-filter__label">{filter.label}</p>
            </div>
            <div className="price-filter__options">
                {filter.options.map((option) => (
                    <ChoiceChip
                        key={option.value}
                        variant={ChoiceChipVariant.CHECKBOX}
                        name={option.label}
                        value={option.value.toString()}
                        checked={isSelected(option.value, selectedOptions)}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                            handlePriceOptionSelect(
                                option.value,
                                event.target.checked
                            )
                        }
                    >
                        {option.label}
                    </ChoiceChip>
                ))}
            </div>
            <div className="price-filter__options--custom-range">
                <Input
                    data-testid={`${dataTestId}__from-input`}
                    label={t('From:')}
                    value={customRange.from}
                    onChange={(event) =>
                        onCustomRangeChange(
                            event.currentTarget.value,
                            CustomRangeField.FROM
                        )
                    }
                    {...commonInputProps}
                />
                <Input
                    data-testid={`${dataTestId}__to-input`}
                    label={t('To:')}
                    value={customRange.to}
                    onChange={(event) =>
                        onCustomRangeChange(
                            event.currentTarget.value,
                            CustomRangeField.TO
                        )
                    }
                    {...commonInputProps}
                />
            </div>
        </StyledPriceFilter>
    );
};
